<template>
  <Sheet v-model:open="isOpen">
    <SheetContent class="overflow-y-auto">
      <SheetHeader padded>
        <SheetTitle size="lg">Edit Custom Fields</SheetTitle>
      </SheetHeader>

      <Separator />

      <SheetBody padded class="flex flex-col gap-3">
        <div v-for="customField in customFieldsList" :key="customField.id">
          <label class="text-sm font-medium text-gray-700">{{ customField.name }}</label>
          <div class="mt-1">
            <ControlledCustomField
              :custom-field="customField"
              :custom-field-value="person.customFields.find(f => f.custom_field_id === customField.id)"
              custom-fieldable-type="Contact"
              :custom-fieldable-id="person.id"
              mode="edit"
              @updated="trackChange"
            />
          </div>
        </div>
        <Button class="mt-6" :disabled="isSaving" @click="handleSave">
          {{ isSaving ? 'Saving...' : 'Save' }}
        </Button>
      </SheetBody>
    </SheetContent>
  </Sheet>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetBody } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import type { Person } from '../Person';
import type { CustomFieldEntity } from '@/api/entity_types/CustomField';
import {
  ControlledCustomField,
  useControlledCustomFieldEditor,
} from '@/components/custom_fields/ControlledCustomField';
import { Separator } from '@/components/ui/separator';

type Props = {
  open: boolean;
  person: Person;
  customFieldsList: CustomFieldEntity[];
};

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:open': [value: boolean];
  saved: [];
}>();

const isOpen = computed({
  get: () => props.open,
  set: (value: boolean) => emit('update:open', value),
});

const { isSaving, trackChange, clearChanges, saveChanges } = useControlledCustomFieldEditor();

const handleSave = async () => {
  const success = await saveChanges();
  if (success) {
    isOpen.value = false;
    emit('saved');
  }
};

defineExpose({
  clearPendingChanges: clearChanges,
});
</script>
